import { useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2, Paperclip, X } from "lucide-react";
import { CmykBar, Crosshair, SectionLabel } from "../Marks";
import { MaskLine, Reveal } from "../Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HELP_OPTIONS = [
  "Production Art / Artwork Preparation",
  "Apparel Printing",
  "Merchandise Production",
  "Artist Collaboration",
  "Business / Organization Apparel",
  "Print Shop Production-Art Support",
  "I'm Not Sure Yet",
];

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  brand_name: "",
  need_help_with: "",
  message: "",
  timeline: "",
  timeline_flexible: false,
};

const Field = ({ label, hint, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="label-mono block text-neutral-500">{label}</span>
    {children}
    {hint && (
      <span className="mt-2 block font-mono-tech text-[0.6rem] text-neutral-600">
        {hint}
      </span>
    )}
  </label>
);

export const Inquiry = () => {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const fileInput = useRef(null);

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true);
    setError("");
    try {
      for (const file of picked) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/upload`, fd);
        setFiles((prev) => [...prev, data]);
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "That file could not be uploaded. You can send it later instead."
      );
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiry`, { ...form, files });
      setDone(true);
      setForm(EMPTY);
      setFiles([]);
    } catch (err) {
      setError(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Something went wrong sending your inquiry. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="inquiry"
      data-testid="section-inquiry"
      className="relative overflow-hidden px-5 py-24 sm:px-8 sm:py-32"
      style={{ background: "var(--ng-charcoal)" }}
    >
      <div
        aria-hidden="true"
        className="halftone-lg pointer-events-none absolute -right-20 -top-10 h-96 w-96 text-[#0055ff]"
      />
      <div className="relative mx-auto max-w-[1600px]">
        <SectionLabel index="03">PROJECT INQUIRY</SectionLabel>

        <div className="mt-8 grid gap-16 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-5">
            <h2 className="display-xl text-[13vw] leading-[0.85] sm:text-[8.5vw] lg:text-[4.8vw]">
              <MaskLine>HAVE AN IDEA?</MaskLine>
              <MaskLine delay={0.08}>
                <span style={{ color: "var(--ng-red)" }}>
                  LET'S MAKE SOMETHING.
                </span>
              </MaskLine>
            </h2>
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-md text-base leading-relaxed text-neutral-300 sm:text-lg">
                Whether you have finished artwork, a rough concept, or aren't
                sure if your file is ready for production, tell us what you're
                working on.
              </p>
              <div className="mt-10 w-32">
                <CmykBar />
              </div>
              <p className="mt-6 font-mono-tech text-xs uppercase leading-relaxed tracking-[0.18em] text-neutral-500">
                Don't have finished artwork?
                <br />
                <span className="text-neutral-200">That's okay.</span>
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <motion.div
                data-testid="inquiry-success"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="relative border border-[#3a3a3a] bg-[#0a0a0a] p-10 sm:p-14"
              >
                <Crosshair size={26} className="text-[#0055ff]" />
                <p className="mt-8 font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-white sm:text-4xl">
                  Thanks for reaching out to No Glyph Studios. We've received
                  your project information and will be in touch.
                </p>
                <button
                  data-testid="inquiry-reset"
                  onClick={() => setDone(false)}
                  className="label-mono mt-10 border-b border-[#e62b2b] pb-1 text-neutral-300 transition-colors hover:text-white"
                >
                  SEND ANOTHER PROJECT
                </button>
              </motion.div>
            ) : (
              <form
                data-testid="inquiry-form"
                onSubmit={onSubmit}
                noValidate={false}
                className="grid gap-8 sm:grid-cols-2"
              >
                <Field label="NAME *" testid="field-name">
                  <input
                    data-testid="inquiry-name"
                    className="ng-field"
                    type="text"
                    required
                    autoComplete="name"
                    placeholder="Your name"
                    value={form.name}
                    onChange={set("name")}
                  />
                </Field>

                <Field label="EMAIL *" testid="field-email">
                  <input
                    data-testid="inquiry-email"
                    className="ng-field"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@email.com"
                    value={form.email}
                    onChange={set("email")}
                  />
                </Field>

                <Field label="PHONE (OPTIONAL)" testid="field-phone">
                  <input
                    data-testid="inquiry-phone"
                    className="ng-field"
                    type="tel"
                    autoComplete="tel"
                    placeholder="—"
                    value={form.phone}
                    onChange={set("phone")}
                  />
                </Field>

                <Field
                  label="BUSINESS / BRAND / ARTIST NAME (OPTIONAL)"
                  testid="field-brand"
                >
                  <input
                    data-testid="inquiry-brand"
                    className="ng-field"
                    type="text"
                    placeholder="—"
                    value={form.brand_name}
                    onChange={set("brand_name")}
                  />
                </Field>

                <div className="sm:col-span-2">
                  <Field label="WHAT DO YOU NEED HELP WITH?" testid="field-help">
                    <select
                      data-testid="inquiry-need-help"
                      className="ng-field"
                      value={form.need_help_with}
                      onChange={set("need_help_with")}
                    >
                      <option value="">Select one</option>
                      {HELP_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <div className="sm:col-span-2">
                  <Field label="TELL US ABOUT YOUR PROJECT *" testid="field-message">
                    <textarea
                      data-testid="inquiry-message"
                      className="ng-field resize-y"
                      rows={6}
                      required
                      placeholder="An idea, a sketch, a finished design — whatever stage you're at."
                      value={form.message}
                      onChange={set("message")}
                    />
                  </Field>
                </div>

                <Field label="WHEN DO YOU NEED IT?" testid="field-timeline">
                  <input
                    data-testid="inquiry-timeline"
                    className="ng-field"
                    type="date"
                    disabled={form.timeline_flexible}
                    value={form.timeline}
                    onChange={set("timeline")}
                  />
                </Field>

                <div className="flex items-end">
                  <label
                    className="flex cursor-pointer items-center gap-3 pb-3"
                    data-testid="field-flexible"
                  >
                    <input
                      data-testid="inquiry-timeline-flexible"
                      type="checkbox"
                      className="h-4 w-4 accent-[#e62b2b]"
                      checked={form.timeline_flexible}
                      onChange={set("timeline_flexible")}
                    />
                    <span className="label-mono text-neutral-300">
                      MY TIMELINE IS FLEXIBLE
                    </span>
                  </label>
                </div>

                <div className="sm:col-span-2">
                  <span className="label-mono block text-neutral-500">
                    UPLOAD YOUR ARTWORK (OPTIONAL)
                  </span>
                  <div className="mt-3 flex flex-wrap items-center gap-4">
                    <button
                      data-testid="inquiry-upload-trigger"
                      type="button"
                      onClick={() => fileInput.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-3 border border-[#3a3a3a] px-5 py-3 font-mono-tech text-xs uppercase tracking-[0.18em] text-neutral-300 transition-colors hover:border-white hover:text-white disabled:opacity-50"
                    >
                      {uploading ? (
                        <Loader2 size={15} className="animate-spin" />
                      ) : (
                        <Paperclip size={15} />
                      )}
                      {uploading ? "UPLOADING" : "ATTACH FILE"}
                    </button>
                    <input
                      ref={fileInput}
                      data-testid="inquiry-file-input"
                      type="file"
                      multiple
                      className="hidden"
                      onChange={onPick}
                      aria-label="Upload your artwork"
                    />
                    <span className="font-mono-tech text-[0.6rem] text-neutral-600">
                      MAX 15MB PER FILE
                    </span>
                  </div>

                  {files.length > 0 && (
                    <ul data-testid="inquiry-file-list" className="mt-4 space-y-2">
                      {files.map((f) => (
                        <li
                          key={f.file_id}
                          className="flex items-center justify-between border border-[#2f2f2f] px-4 py-2.5"
                        >
                          <span className="flex items-center gap-3 font-mono-tech text-xs text-neutral-300">
                            <Check size={14} className="text-[#0055ff]" />
                            {f.original_filename}
                          </span>
                          <button
                            type="button"
                            data-testid={`remove-file-${f.file_id}`}
                            aria-label={`Remove ${f.original_filename}`}
                            onClick={() =>
                              setFiles((p) =>
                                p.filter((x) => x.file_id !== f.file_id)
                              )
                            }
                            className="text-neutral-500 transition-colors hover:text-[#e62b2b]"
                          >
                            <X size={15} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {error && (
                  <p
                    data-testid="inquiry-error"
                    role="alert"
                    className="font-mono-tech text-xs text-[#e62b2b] sm:col-span-2"
                  >
                    {error}
                  </p>
                )}

                <div className="sm:col-span-2">
                  <button
                    data-testid="inquiry-submit-button"
                    type="submit"
                    disabled={submitting}
                    className="group flex w-full items-center justify-center gap-4 px-8 py-6 font-display text-3xl font-extrabold uppercase tracking-tight text-white transition-transform duration-300 hover:-translate-y-1 disabled:translate-y-0 disabled:opacity-60 sm:text-4xl"
                    style={{ background: "var(--ng-red)" }}
                  >
                    {submitting && <Loader2 size={24} className="animate-spin" />}
                    {submitting ? "SENDING" : "START THE CONVERSATION"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
